import { PILL_COLORS } from "components/ui/Pill/pillColors";

const VACCINE_STATUS_CONFIG = {
  SAVED: {
    label: "Saved (Manual)",
    simplifiedLabel: "Saved",
    colors: PILL_COLORS.statusSuccess,
  },
  HL_SUBMITTING_NIIS: {
    label: "Submitting to NIIS",
    simplifiedLabel: "Processing",
    colors: PILL_COLORS.statusPending,
  },
  HL_SUBMIT_FAILED_NIIS: {
    label: "NIIS Rejection",
    simplifiedLabel: "Rejected",
    colors: PILL_COLORS.statusError,
  },
  HL_PROCESSING_NIIS: {
    label: "Processing at NIIS",
    simplifiedLabel: "Processing",
    colors: PILL_COLORS.statusPending,
  },
  HL_ERROR_NIIS: {
    label: "NIIS Rejection",
    simplifiedLabel: "Rejected",
    colors: PILL_COLORS.statusError,
  },
  HL_SUBMITTING_PCRS: {
    label: "Submitting to PCRS",
    simplifiedLabel: "Processing",
    colors: PILL_COLORS.statusPending,
  },
  HL_SUBMIT_FAILED_PCRS: {
    label: "PCRS Rejection",
    simplifiedLabel: "Rejected",
    colors: PILL_COLORS.statusError,
  },
  HL_PROCESSING_PCRS: {
    label: "Processing at PCRS",
    simplifiedLabel: "Processing",
    colors: PILL_COLORS.statusPending,
  },
  HL_ERROR_PCRS: {
    label: "PCRS Rejection",
    simplifiedLabel: "Rejected",
    colors: PILL_COLORS.statusError,
  },
  HL_COMPLETED: {
    label: "Completed",
    simplifiedLabel: "Completed",
    colors: PILL_COLORS.statusSuccess,
  },

  // Added by KIN-1787 / KIN-1780, which split three cases out of the states above. All three mean
  // "nothing is going to happen here without a person", which is why they share a simplified label
  // of their own rather than borrowing "Processing" — grouping them there would restate the exact
  // thing the backend change removed, that a record nobody is waiting on looks like one in flight.
  HL_NO_RESPONSE_NIIS: {
    label: "No response from NIIS",
    simplifiedLabel: "No response",
    colors: PILL_COLORS.statusWarning,
  },
  HL_NO_RESPONSE_PCRS: {
    label: "No response from PCRS",
    simplifiedLabel: "No response",
    colors: PILL_COLORS.statusWarning,
  },
  HL_NOT_SUBMITTED: {
    label: "Not submitted",
    simplifiedLabel: "Not submitted",
    colors: PILL_COLORS.statusWarning,
  },

  // A cancellation is a conversation with NIIS and PCRS, not an instant (KIN-1814/1815).
  // Dalen's rule decides which of the three a record is in: "it is not cancelled until
  // positive async ack is done." They are kept apart because they mean opposite things to
  // whoever is looking — one is in flight, one is settled, one needs a person.
  HL_CANCELLING: {
    label: "Cancelling",
    simplifiedLabel: "Cancelling",
    colors: PILL_COLORS.statusPending,
  },
  HL_CANCELLED: {
    label: "Cancelled",
    simplifiedLabel: "Cancelled",
    colors: PILL_COLORS.statusNoStatus,
  },
  HL_CANCEL_FAILED: {
    label: "Cancellation rejected",
    simplifiedLabel: "Cancellation rejected",
    colors: PILL_COLORS.statusError,
  },
};

const normalizeStatus = (status) =>
  status
    .toUpperCase()
    .replace(/[()]/g, "")
    .replace(/\s+/g, "_")
    .trim();

const getStatusConfig = (status) => {
  if (!status) return null;
  return VACCINE_STATUS_CONFIG[normalizeStatus(status)] ?? null;
};

export const getImmunizationStatusColors = (status) => {
  return getStatusConfig(status)?.colors ?? PILL_COLORS.statusNoStatus;
};

export const formatImmunizationStatus = (status) => {
  if (!status) return "No status";
  return getStatusConfig(status)?.label ?? status;
};

export const formatImmunizationStatusSimplified = (status) => {
  if (!status) return "No status";
  return getStatusConfig(status)?.simplifiedLabel ?? status;
};

// Whether a status means the submission was refused, or is still in flight.
//
// The mapping from backend enum to "is this a rejection" belongs here, in the one file
// that already knows the vocabulary and already normalises it. Spread across call sites
// it drifted: the tenant-wide table compared `status` against the bare strings
// "warning" and "error", which the backend never sends, so its rejection styling and
// its "View status details" action were dead for every row in every state (KIN-1719 §2).
const REJECTED_STATUSES = Object.keys(VACCINE_STATUS_CONFIG).filter(
  (status) => VACCINE_STATUS_CONFIG[status].simplifiedLabel === "Rejected"
);
const PENDING_STATUSES = Object.keys(VACCINE_STATUS_CONFIG).filter(
  (status) => VACCINE_STATUS_CONFIG[status].simplifiedLabel === "Processing"
);
// Neither refused nor in flight: sent and unanswered past the point one can be expected, or never
// sent at all. Distinct from pending because a clinician can do something about these and there is
// nothing to wait for, and distinct from rejected because no receiver has said no.
const ACTION_REQUIRED_STATUSES = Object.keys(VACCINE_STATUS_CONFIG).filter((status) =>
  ["No response", "Not submitted"].includes(
    VACCINE_STATUS_CONFIG[status].simplifiedLabel
  )
);

// A refused cancellation is a refusal, and the most consequential one there is: the dose
// stands on a national register while the practice believes it withdrawn. Listed here
// rather than given the "Rejected" simplified label, because the label has to say which
// message was refused — a clinician reading "Rejected" on a withdrawn dose would take it
// for a refused submission, which is the opposite situation.
const REJECTED_OR_CANCEL_FAILED = [...REJECTED_STATUSES, "HL_CANCEL_FAILED"];

// A cancellation in flight is as unsettled as a submission in flight.
const PENDING_OR_CANCELLING = [...PENDING_STATUSES, "HL_CANCELLING"];

export const isImmunizationRejected = (status) =>
  !!status && REJECTED_OR_CANCEL_FAILED.includes(normalizeStatus(status));

export const isImmunizationPending = (status) =>
  !!status && PENDING_OR_CANCELLING.includes(normalizeStatus(status));

/**
 * A dose the practice has withdrawn and both receivers have confirmed.
 *
 * Still shown, never removed: the child was given the vaccine and it was reported to
 * NIIS, and withdrawing the claim does not unmake the clinical event. A vaccination
 * history that silently loses a dose is worse than one carrying a struck-through entry.
 */
export const isImmunizationCancelled = (status) =>
  !!status && normalizeStatus(status) === "HL_CANCELLED";

export const isImmunizationActionRequired = (status) =>
  !!status && ACTION_REQUIRED_STATUSES.includes(normalizeStatus(status));

// The marker a submit or resubmit returns on a 200 in `healthlinkResponse`, which despite the
// name is not an acknowledgement: it says which of the two messages were sent (KIN-1753/KIN-1786).
//
// Four of the five mean the record is finished and read as success. The fifth does not, and the
// difference is the point: VACCINATION_REPORT_ONLY means the claim was already accepted, while
// VACCINATION_REPORT_ONLY_NOT_CLAIMABLE means no claim was ever sent because the patient has no
// PPSN. Showing the second as success reports an unpaid dose as a completed one.
const SUBMISSION_OUTCOMES = {
  OK: {
    severity: "success",
    message: "Vaccine submitted to Healthlink successfully",
  },
  VACCINATION_REPORT_ONLY: {
    severity: "success",
    message:
      "Submitted to NIIS. The reimbursement claim had already been accepted, so only the report was sent.",
  },
  REIMBURSEMENT_CLAIM_ONLY: {
    severity: "success",
    message:
      "Reimbursement claim submitted. The NIIS report had already been accepted, so only the claim was sent.",
  },
  NOTHING_TO_CANCEL: {
    severity: "neutral",
    message: "There was nothing to withdraw — no message had been accepted.",
  },
  VACCINATION_REPORT_ONLY_NOT_CLAIMABLE: {
    severity: "warning",
    message:
      "Reported to NIIS. No reimbursement claim was sent because this patient has no PPSN — add it to the patient record and submit again.",
  },
};

const DEFAULT_SUBMISSION_OUTCOME = SUBMISSION_OUTCOMES.OK;

// An unrecognised marker is reported as the plain success it arrived as: a value added later
// should not be shown as a warning on the strength of being unknown.
export const getSubmissionOutcome = (healthlinkResponse) =>
  SUBMISSION_OUTCOMES[healthlinkResponse] ?? DEFAULT_SUBMISSION_OUTCOME;

export const isClaimBlockedByMissingPpsn = (healthlinkResponse) =>
  healthlinkResponse === "VACCINATION_REPORT_ONLY_NOT_CLAIMABLE";

// How one receiver answered, independent of the other (KIN-1824/1826). The backend sends
// `niis` and `pcrs` on every immunisation, both drawn from one receiver-agnostic enum, so a
// single map serves both rather than a NIIS/PCRS pair of everything.
//
// This is the vocabulary the collapsed `status` cannot express. A dose NIIS accepted and PCRS
// refused collapses to HL_ERROR_PCRS — the correct single answer, and silent about the dose
// being on the national register. Both facts matter to a clinician: it will not be paid for,
// and it must not be given again.
const RECEIVER_STATE_CONFIG = {
  // Nothing is ever sent to this receiver — a manual entry. Deliberately distinct from
  // NOT_SENT: this is not outstanding work, and most historical doses on a record are here.
  NOT_APPLICABLE: { label: "Not applicable", colors: PILL_COLORS.statusNoStatus },
  NOT_SENT: { label: "Not sent", colors: PILL_COLORS.statusWarning },
  // Deliberately not sent and never will be — no PPSN on the patient. Terminal, and the one
  // state a clinician can clear themselves by completing the patient record.
  WITHHELD: { label: "Withheld", colors: PILL_COLORS.statusWarning },
  SEND_FAILED: { label: "Send failed", colors: PILL_COLORS.statusError },
  SUBMITTING: { label: "Submitting", colors: PILL_COLORS.statusPending },
  PROCESSING: { label: "Processing", colors: PILL_COLORS.statusPending },
  NO_RESPONSE: { label: "No response", colors: PILL_COLORS.statusWarning },
  ACCEPTED: { label: "Accepted", colors: PILL_COLORS.statusSuccess },
  REJECTED: { label: "Rejected", colors: PILL_COLORS.statusError },
};

export const formatReceiverState = (state) =>
  RECEIVER_STATE_CONFIG[state]?.label ?? state ?? "-";

export const getReceiverStateColors = (state) =>
  RECEIVER_STATE_CONFIG[state]?.colors ?? PILL_COLORS.statusNoStatus;

/**
 * Whether this dose was ever a candidate for Healthlink at all.
 *
 * A manual entry reports NOT_APPLICABLE at both receivers, and showing it a submission
 * panel full of "Not applicable" is noise on the majority of a patient's history.
 */
export const hasHealthlinkSubmission = (immunisation) =>
  immunisation?.niis?.state !== "NOT_APPLICABLE" ||
  immunisation?.pcrs?.state !== "NOT_APPLICABLE";

// Receiver states a resubmission can actually change. ACCEPTED needs nothing, SUBMITTING
// and PROCESSING are already in flight, and NOT_APPLICABLE is a manual entry that is never
// sent to anyone.
//
// WITHHELD is deliberately in this list. It is terminal only while its precondition holds,
// and completing the patient's PPSN is exactly the fix a clinician makes before retrying —
// the flow KIN-1789 and the PPSN dialog exist for.
const RETRYABLE_RECEIVER_STATES = [
  "NOT_SENT",
  "WITHHELD",
  "SEND_FAILED",
  "NO_RESPONSE",
  "REJECTED",
];

const CANCELLATION_STATUSES = [
  "HL_CANCELLING",
  "HL_CANCELLED",
  "HL_CANCEL_FAILED",
];

/**
 * Whether offering to send this dose to Healthlink again makes sense.
 *
 * Both tables previously asked `healthlinkResponseCode !== "200"`. That column never holds
 * "200" — it holds the receiver's HL7 MSA.1 code (AA/AE/AR) or null — so the test was always
 * true and the action appeared on every row, including doses both receivers had accepted.
 * Resubmitting one of those sends a duplicate report for a dose already on the national
 * register.
 *
 * It read one of the three mirror columns written at submit time and never updated by
 * asynchronous ingestion, which KIN-1826 replaced precisely because they contradict the real
 * outcome. This reads the per-receiver state instead (KIN-1824), which both list payloads
 * carry.
 */
export const canSubmitToHealthlink = (immunisation) => {
  if (!immunisation?.vaccineCodeId) return false;

  const status = immunisation.status
    ? normalizeStatus(immunisation.status)
    : null;

  // A dose the practice has withdrawn, or is withdrawing: sending it again is the opposite
  // of what was asked for.
  if (status && CANCELLATION_STATUSES.includes(status)) return false;

  return [immunisation.niis?.state, immunisation.pcrs?.state].some((state) =>
    RETRYABLE_RECEIVER_STATES.includes(state)
  );
};

/** A HealthLink acknowledgement is only an acceptance when it is exactly "ACK". */
export const isAckAccepted = (ackStatus) => ackStatus === "ACK";

// The third granularity: one transmission's own technical outcome, from
// /healthlink-status. Separate from the two above on purpose — this vocabulary describes a
// message, not a dose. A cancelled dose has six of these rows and only one collapsed status.
const MESSAGE_STATUS_CONFIG = {
  PENDING: { label: "Pending", colors: PILL_COLORS.statusPending },
  SUBMITTING: { label: "Submitting", colors: PILL_COLORS.statusPending },
  SUBMITTED: { label: "Submitted", colors: PILL_COLORS.statusPending },
  PROCESSING: { label: "Processing", colors: PILL_COLORS.statusPending },
  RETRYING: { label: "Retrying", colors: PILL_COLORS.statusWarning },
  ACK_RECEIVED: { label: "Accepted", colors: PILL_COLORS.statusSuccess },
  NAK_RECEIVED: { label: "Refused", colors: PILL_COLORS.statusError },
  FAILED: { label: "Failed", colors: PILL_COLORS.statusError },
};

// The acknowledgement outranks `status`, because `status` is written when the message is sent
// and is not rewritten when the answer arrives. Verified on UAT: an accepted VXU sits at
// SUBMITTED / ACK forever, so reading `status` alone reports an accepted message as merely
// sent — directly contradicting the receiver block above it, which is the contradiction this
// whole ticket exists to remove.
//
// It also separates the two things FAILED covers: the receiver read the message and refused
// it (NAK), and the send never landed at all (no acknowledgement). Collapsing those is the
// confusion KIN-1829 fixed one layer up.
const ACCEPTED_OUTCOME = { label: "Accepted", colors: PILL_COLORS.statusSuccess };
const REFUSED_OUTCOME = { label: "Refused", colors: PILL_COLORS.statusError };
const SEND_FAILED_OUTCOME = {
  label: "Not sent",
  colors: PILL_COLORS.statusError,
};

const getMessageOutcome = (message) => {
  if (message?.ackStatus === "NAK") return REFUSED_OUTCOME;

  if (isAckAccepted(message?.ackStatus)) return ACCEPTED_OUTCOME;

  if (message?.status === "FAILED") return SEND_FAILED_OUTCOME;

  return MESSAGE_STATUS_CONFIG[message?.status];
};

export const formatMessageStatus = (message) =>
  getMessageOutcome(message)?.label ?? message?.status ?? "-";

export const getMessageStatusColors = (message) =>
  getMessageOutcome(message)?.colors ?? PILL_COLORS.statusNoStatus;

// Which receiver a message went to. `messageType` serialises as the enum's display name
// ("PCRS Reimbursement"), not its constant — verified against UAT, and the field's own Swagger
// description says otherwise. Both are accepted so a corrected description cannot break this.
const MESSAGE_RECEIVER_LABELS = {
  "Vaccination Update Report": "NIIS",
  VACCINATION_REPORT: "NIIS",
  "PCRS Reimbursement": "PCRS",
  PCRS_REIMBURSEMENT: "PCRS",
};

export const formatMessageReceiver = (messageType) =>
  MESSAGE_RECEIVER_LABELS[messageType] ?? messageType ?? "-";

// OBR.25 F / C / X. Null on rows written before z_243, which the backend reads as an
// original, so it is labelled as one rather than left blank.
const MESSAGE_ACTION_LABELS = {
  CREATE: "Original",
  UPDATE: "Correction",
  DELETE: "Cancellation",
};

export const formatMessageAction = (actionType) =>
  MESSAGE_ACTION_LABELS[actionType ?? "CREATE"] ?? actionType;
